import { Injectable } from '@angular/core';
import { BehaviorSubject, catchError, map, Observable, of } from 'rxjs';
import { Endpoints } from 'src/environments/endpoints';
import {
  IInsuranceRequest,
  IInsuranceDic,
  InsurancesDic,
  IInsurance,
} from '../components/configuration-panel/interfaces/aseguradoras';
import { HttpParams } from '@angular/common/http';
import { ConsumeService } from './consume.service';
import { IResponse } from '../components/configuration-panel/interfaces/IResponse';
import { catchErrorInsurances } from '../components/configuration-panel/helpers/catchErrorCotizador';
import { DefaultResponseProduct } from '../components/configuration-panel/helpers/catchErrorProduct';
import { IProductDetail } from '../components/configuration-panel/interfaces/product';
import { IServicesPlus } from '../model/http/servicesPlus.model';
import { sendBodyModifiedBy } from '../utils/user.interceptor';
import { ICreditAmotization } from '../pages/buro/interfaces/ICreditAmortization';
import { environment } from 'src/environments/environment';
import { IInsuranceRequestCredit } from '../components/configuration-panel/interfaces/aseguradoras';
import { SecurityService } from './security.service';

@Injectable({
  providedIn: 'root',
})
export class CotizadorService {
  insurnaces = new BehaviorSubject<IInsurance[][]>([]);
  insuranceDic = new BehaviorSubject<IInsuranceDic>(InsurancesDic[4]);

  constructor(
    private consume: ConsumeService,
    private securityService: SecurityService
  ) { }

  getInsuranceInfo(req: IInsuranceRequest): Observable<IResponse> {

    const { idState, cpTip, idTip, year, priceWithIva, insurance, quoterType, flowType, version, userId, idLeasetech } = req;

    let params = new HttpParams();
    params = params.append('idState', idState);
    params = params.append('cpTip', cpTip);
    params = params.append('idTip', idTip);
    params = params.append('year', year);
    params = params.append('priceWithIva', priceWithIva);
    params = params.append('insurance', insurance);
    params = params.append('quoterType', quoterType);
    // Estos tres parametros unicamente se utilizan para flujo B espcificamente para FORD: Campaign
    // Es para diferenciar el costo del seguro por version y el tipo de flujo
    params = params.append('flowType', flowType);
    params = params.append('version', version);
    params = params.append('userId', userId);
    params = params.append('idLeasetech', idLeasetech);


    return this.consume
      .httpGet<IResponse>(
        `${Endpoints.cotizadorV2.base}/compare-insurance?${params.toString()}`
      )
      .pipe(
        map((resp) => resp),
        catchError((err) => of(catchErrorInsurances(err)))
      );
  }

  getCreditInsuranceInfo(req: IInsuranceRequestCredit): Observable<IResponse> {
    const { idState, cpTip, idTip, year, priceWithIva, insurance, quoterType } =
      req;

    let params = new HttpParams();
    params = params.append('idState', idState);
    params = params.append('cpTip', cpTip);
    params = params.append('idTip', idTip);
    params = params.append('year', year);
    params = params.append('priceWithIva', priceWithIva);
    params = params.append('insurance', insurance);
    params = params.append('quoterType', quoterType);

    return this.consume
      .httpGet<IResponse>(
        `${Endpoints.cotizadorV3.base}/compare-credit-insurance?${params.toString()}`
      )
      .pipe(
        map((resp) => resp),
        catchError((err) => of(catchErrorInsurances(err)))
      );
  }

  getParamsQuote(
    quoterId: string,
    concept: string,
    period: string,
    extraPeriods: number = 1 
  ): Observable<IProductDetail> {
    return this.consume
      .httpGet<IProductDetail>(
        `${Endpoints.cotizadorV2.base}/product?quoterId=${quoterId}&concept=${concept}&period=${period}&extraPeriods=${extraPeriods}`
      )
      .pipe(
        map((resp: any) => {
          const dataToStore = resp.data.data;
          const encryptedData = this.securityService.encryptData(dataToStore, false);
          localStorage.setItem('_quotationdata', encryptedData); 
         return resp.data.data;
        }),
        catchError((err) => of(DefaultResponseProduct))
      );
  }

  /**
   * Obtiene los parametros del crédito
   * @param {string} quoterId
   * @param {string} concept
   * @param {string} period
   * @return {*}  {Observable<any>}
   * @memberof CotizadorService
   * @author victor.mendez
   */
  getParamsRequest(idCredit: string): Observable<any> {
    //TODO: Cambiar el funcionamiento para flujo B 
    let _idC = { idCredit: idCredit };
    return this.consume.httpPost<any>(
      `${Endpoints.buro.documentManagmentUrlPublicv3}/downloadCreditRequest/3`, _idC)
      .pipe(
        map((resp: any) => {
          return resp.data.request;
        }),
        catchError((err) => of(DefaultResponseProduct))
      );
  }

  getNewQuote(body: any) {
    
    return this.consume
      .httpPost(`${Endpoints.cotizadorV2.base}/quoter`, body)
      .pipe(map((resp: any) => resp.data));
  }

  getRenting(
    id: string,
    km: number,
    cTiD: string,
    quoterType: string,
    flowType: string
  ): Observable<any> {
    return this.consume
      .httpGet(
        `${Endpoints.cotizadorV2.base}/maximum-ri?carId=${id}&km=${km}&customerTypeId=${cTiD}&quoterType=${quoterType}&flowType=${flowType}`)
      .pipe(map((resp: any) => resp.data));
  }

  getServicePlus(data: IServicesPlus): Observable<any> {
    let {
      carId,
      km,
      cTiD,
      typeSuitCar,
      stateId,
      insurance,
      cpTip,
      quoterType,
      flowType,
    } = data;

    return this.consume
      .httpGet(
        `${Endpoints.cotizadorV2.base}/services-plus?carId=${carId}&km=${km}&customerTypeId=${cTiD}&typeSustituteCar=${typeSuitCar}&stateId=${stateId}&insurance=${insurance}&cpTip=${cpTip}&quoterType=${quoterType}&flowType=${flowType}`
      )
      .pipe(map((resp: any) => resp.data));
  }

  getRelatedCars(param: string): Observable<any> {
    return this.consume.httpGet(
      `${Endpoints.cotizadorV2.base}/affinity-score?${param}`
    );
  }

  generateQuoter(request: any): Observable<any> {
    return this.consume
      .httpPost(`${Endpoints.cotizador.base}/quoter`, request)
      .pipe(map((resp: any) => resp.data));
  }

  preauthorize(request: any): Observable<any> {
    return this.consume.httpPost(
      `${Endpoints.cotizadorV2.base}/preauthorize`,
      request,
      null,
      sendBodyModifiedBy()
    );
  }

  existingProcesses(idKeycloak: any): Observable<any> {
    return this.consume.httpGet(
      `${Endpoints.cotizadorV2.base}/step/:${idKeycloak}`
    );
  }

  updateQuote(idKeycloak: string, body: any) {
    return this.consume.httpPost(
      `${Endpoints.solicitud.base}/update-quote/${idKeycloak}`,
      body,
      null,
      sendBodyModifiedBy()
    );
  }

  finishProccesAndQuoter(id: string, ofDesactiveCar: boolean) {
    return this.consume.httpPut(`${Endpoints.creditV2.base}/:${id}`, {
      ofDesactiveCar,
    });
  }

  getPurchaseValue(id: string, km: number): Observable<any> {
    let flowtype = environment.templateSuffix === '' ? 'A' : 'B';
    return this.consume
      .httpGet(
        `${Endpoints.cotizadorV2.base}/afin/purchasevalue?carId=${id}&km=${km}&flowType=${flowtype}`
      )
      .pipe(map((resp: any) => resp.data));
  }
  
  getCreditAmortization(id: string, period: number, concept: string): Observable<any> {
    return this.consume
      .httpGet<{data: ICreditAmotization[]}>(
        `${Endpoints.cotizadorV3.base}/generate_credit_amortization/${id}`, {
          period,
          concept,
          round: 1
        }
      )
      .pipe(map((resp) => resp.data));
  }

}
